
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useLanguage } from '@/hooks/useLanguage';

interface NewsItemProps {
  news: {
    title: string;
    link: string;
    pubDate?: string;
    date?: string;
    source?: string;
  };
}

export function NewsListItem({ news }: NewsItemProps) {
  const { language, t } = useLanguage();

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return format(date, 'dd MMM yyyy', {
        locale: language === 'pl' ? pl : enUS,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // Use pubDate if available, otherwise try date
  const displayDate = formatDate(news.pubDate || news.date);

  return (
    <div className="group h-full flex flex-col flex-1 min-h-0">
      <a
        href={news.link}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col flex-1 justify-center p-2 hover:bg-muted/50 rounded-lg group-hover:text-primary transition-colors min-h-0"
      >
        <div className="flex justify-between items-start gap-2 mb-1 shrink-0">
          <h3 className="font-medium text-sm leading-tight flex-1 group-hover:underline line-clamp-2">{news.title}</h3>
          <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-auto pt-1 shrink-0">
          <span className="truncate max-w-[60%]">{news.source || t('common.unknown_source')}</span>
          <span className="flex-shrink-0">{displayDate}</span>
        </div>
      </a>
    </div>
  );
}
